import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, UserPlus, RefreshCw, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import { hashPassword } from '../../utils/crypto';
import { useAuth } from '../../context/AuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const randomPassword = () => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  return Array.from({ length: 12 }, () => charset[Math.floor(Math.random() * charset.length)]).join('');
};

// Creates the user account for a pending shipment whose exporter has no
// user yet, without leaving the approval queue.
const QuickCreateUserModal = ({ exporterName, notice, onClose, onCreated }) => {
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({
    customerName: exporterName || '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '', api: '' }));
  };

  // Nothing is pre-filled here — the admin types the username and either sets a
  // password or asks for one. A generated password is shown, not hidden, so it
  // can be copied and passed on.
  const generatePassword = () => {
    set('password', randomPassword());
    setShowPassword(true);
  };

  const validate = () => {
    const next = {};
    if (!form.customerName.trim() || form.customerName.trim().length < 2) next.customerName = 'Enter the name';
    if (!EMAIL_RE.test(form.email.trim())) next.email = 'Enter a valid email address';
    if (!/^[0-9]{10}$/.test(form.phoneNumber)) next.phoneNumber = 'Phone number must be 10 digits';
    if (form.username.trim().length < 5) next.username = 'Username must be at least 5 characters';
    if (form.password.length < 6) next.password = 'Password must be at least 6 characters';
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length) { setErrors(next); return; }

    setSaving(true);
    setErrors({});
    try {
      const res = await apiCall(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim().toLowerCase(),
          Name: form.customerName.trim(),
          phNum: `+91${form.phoneNumber}`,
          role: 'user',
          status: 'active',
          password: await hashPassword(form.password),
          website: '',
          created_by: currentUser?.id || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setErrors({ api: data?.message || `Could not create the user (HTTP ${res.status})` });
        return;
      }
      const created = data?.data?.user || data?.data || data?.user || {};
      toast.success(`User "${form.customerName.trim()}" created`);
      onCreated?.({
        id: created.id ?? created.userId ?? null,
        name: form.customerName.trim(),
      });
      onClose();
    } catch (err) {
      console.error('Quick create user error:', err);
      setErrors({ api: 'Unable to reach the server. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, props = {}) => (
    <div>
      <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
        {label} <span className="text-red-500">*</span>
      </label>
      <input
        value={form[name]}
        onChange={(e) => set(name, props.transform ? props.transform(e.target.value) : e.target.value)}
        disabled={saving}
        {...props.input}
        className={`w-full px-3.5 py-2.5 rounded-xl text-[13px] font-semibold outline-none transition bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-100`}
      />
      {errors[name] && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors[name]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        noValidate
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden bg-white border border-slate-200 dark:bg-[#0b1220] dark:border-slate-800"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300 flex items-center justify-center flex-shrink-0">
              <UserPlus size={18} className="" />
            </span>
            <div>
              <h3 className="text-[16px] font-bold text-slate-900 dark:text-white leading-tight">Create User</h3>
              <p className="text-[12px] text-slate-500 dark:text-slate-400">This user will be assigned to the shipment</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {notice && (
            <div className="flex gap-2.5 px-3.5 py-3 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-700">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 dark:text-amber-200 leading-snug">{notice}</p>
            </div>
          )}
          {field('customerName', 'Name', { input: { placeholder: 'Full name as on the LEO', autoComplete: 'off' } })}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('email', 'Email', { input: { type: 'email', placeholder: 'user@example.com', autoComplete: 'off' } })}
            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Phone <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500 dark:text-gray-400 pointer-events-none">+91</span>
                <input
                  value={form.phoneNumber}
                  onChange={(e) => set('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  disabled={saving}
                  inputMode="numeric"
                  placeholder="9876543210"
                  autoComplete="off"
                  className={`w-full pl-12 pr-3.5 py-2.5 rounded-lg border-2 text-sm font-medium outline-none transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 ${
                    errors.phoneNumber ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                  }`}
                />
              </div>
              {errors.phoneNumber && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.phoneNumber}</p>}
            </div>
          </div>
          {field('username', 'Username', { transform: (v) => v.replace(/\s/g, ''), input: { placeholder: 'Unique login name', autoComplete: 'new-password' } })}

          <div>
            <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                disabled={saving}
                placeholder="Set a password or generate one"
                autoComplete="new-password"
                className={`w-full px-3.5 py-2.5 pr-20 rounded-lg border-2 text-sm font-medium outline-none transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                  errors.password ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                }`}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-0.5">
                <button type="button" onClick={generatePassword} title="Generate a strong password"
                  className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                  <RefreshCw size={15} />
                </button>
                <button type="button" onClick={() => setShowPassword(v => !v)} title={showPassword ? 'Hide' : 'Show'}
                  className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {errors.password ? (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.password}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                <button type="button" onClick={generatePassword} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                  Generate a strong password
                </button>
                {' '}— share it with the user, it isn't shown again.
              </p>
            )}
          </div>

          {errors.api && (
            <div className="px-3.5 py-2.5 rounded-lg text-sm bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300">
              {errors.api}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-gray-50 dark:bg-gray-800/60">
          <button type="button" onClick={onClose} disabled={saving}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
            Create & Assign
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuickCreateUserModal;
